import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X, LayoutGrid, Map } from "lucide-react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { useLanguage } from "@/lib/LanguageContext";
import Navbar from "@/components/Navbar";
import ListingCard from "@/components/ListingCard";
import ListingsMap from "@/components/ListingsMap";
import GlobeScene from "@/components/GlobeScene";
import Reveal from "@/components/Reveal";
import Footer from "@/components/Footer";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ListingsPage() {
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const [listings, setListings] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [requestVersion, setRequestVersion] = useState(0);
  const [sortBy, setSortBy] = useState("default");
  const [guestFilter, setGuestFilter] = useState("all");
  const [viewMode, setViewMode] = useState("grid");
  const query = searchParams.get("q") || "";

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError(false);
    (async () => {
      try {
        const res = await axios.get(`${API}/listings`, { signal: controller.signal });
        setListings(res.data.listings || []);
      } catch {
        if (!controller.signal.aborted) {
          setListings([]);
          setLoadError(true);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [requestVersion]);

  useEffect(() => {
    let result = [...listings];
    if (query) {
      const q = query.toLowerCase();
      result = result.filter(
        (l) => l.name.toLowerCase().includes(q) || l.city.toLowerCase().includes(q) || l.description?.toLowerCase().includes(q)
      );
    }
    if (guestFilter !== "all") result = result.filter((l) => l.personCapacity >= parseInt(guestFilter));
    if (sortBy === "price-asc") result.sort((a, b) => a.price - b.price);
    else if (sortBy === "price-desc") result.sort((a, b) => b.price - a.price);
    else if (sortBy === "capacity") result.sort((a, b) => b.personCapacity - a.personCapacity);
    setFiltered(result);
  }, [listings, sortBy, guestFilter, query]);

  return (
    <div data-testid="listings-page" className="min-h-screen bg-[#FAFAFA]">
      <Helmet>
        <title>{`${t("listings_page_title")} | Oona Flats Wien`}</title>
        <meta name="description" content={t("listings_page_title")} />
      </Helmet>
      <Navbar />

      {/* 3D Globe Header */}
      <div className="pt-16">
        <GlobeScene listings={listings} />
      </div>

      <div className="pb-20 md:pb-24 px-5 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="mb-10 md:mb-12 pt-8 md:pt-12">
              <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#86868B] mb-2">
                {query ? `${t("listings_search_prefix")}: "${query}"` : t("listings_page_label")}
              </p>
              <h1 className="text-3xl md:text-6xl font-semibold tracking-tighter text-[#1D1D1F]" style={{ fontFamily: 'Outfit, sans-serif' }}>{t("listings_page_title")}</h1>
              <p data-testid="listings-status" className="mt-3 md:mt-4 text-[#86868B] text-base md:text-lg">
                {loading ? t("listings_loading") : loadError ? t("listings_load_error") : `${filtered.length} ${t("listings_available")}`}
              </p>
            </div>
          </Reveal>

          <div data-testid="listings-filters" className="flex flex-wrap items-center gap-2 md:gap-3 mb-8 md:mb-10">
            <div className="hidden sm:flex items-center gap-2 text-sm text-[#86868B]"><SlidersHorizontal className="w-4 h-4" /><span className="font-medium">{t("filter_label")}</span></div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger data-testid="sort-select" className="w-[140px] md:w-[180px] rounded-full border-black/10 bg-white text-sm"><SelectValue placeholder={t("filter_sort_default")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="default">{t("filter_sort_default")}</SelectItem>
                <SelectItem value="price-asc">{t("filter_sort_price_asc")}</SelectItem>
                <SelectItem value="price-desc">{t("filter_sort_price_desc")}</SelectItem>
                <SelectItem value="capacity">{t("filter_sort_capacity")}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={guestFilter} onValueChange={setGuestFilter}>
              <SelectTrigger data-testid="guest-filter-select" className="w-[130px] md:w-[160px] rounded-full border-black/10 bg-white text-sm"><SelectValue placeholder={t("filter_guests_all")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("filter_guests_all")}</SelectItem>
                <SelectItem value="2">{t("filter_guests_2")}</SelectItem>
                <SelectItem value="4">{t("filter_guests_4")}</SelectItem>
                <SelectItem value="6">{t("filter_guests_6")}</SelectItem>
              </SelectContent>
            </Select>
            {(sortBy !== "default" || guestFilter !== "all") && (
              <button data-testid="clear-filters-button" onClick={() => { setSortBy("default"); setGuestFilter("all"); }} className="flex items-center gap-1 text-sm text-[#86868B] hover:text-[#1D1D1F] transition-colors">
                <X className="w-3.5 h-3.5" />{t("filter_reset")}
              </button>
            )}
            <div className="ml-auto flex items-center bg-white rounded-full border border-black/10 p-1">
              <button data-testid="view-grid-button" onClick={() => setViewMode("grid")} className={`p-2 rounded-full transition-all ${viewMode === "grid" ? "bg-black text-white" : "text-[#86868B] hover:text-[#1D1D1F]"}`}><LayoutGrid className="w-4 h-4" /></button>
              <button data-testid="view-map-button" onClick={() => setViewMode("map")} className={`p-2 rounded-full transition-all ${viewMode === "map" ? "bg-black text-white" : "text-[#86868B] hover:text-[#1D1D1F]"}`}><Map className="w-4 h-4" /></button>
            </div>
          </div>

          {loadError ? (
            <div data-testid="listings-load-error" role="alert" className="text-center py-20">
              <p className="text-xl text-[#1D1D1F]">{t("listings_load_error")}</p>
              <button
                data-testid="listings-retry-button"
                onClick={() => setRequestVersion((version) => version + 1)}
                className="mt-5 rounded-full bg-black px-6 py-3 text-sm font-medium text-white btn-press"
              >
                {t("listings_retry")}
              </button>
            </div>
          ) : viewMode === "map" ? (
            <ListingsMap listings={filtered} />
          ) : loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (<div key={i} className="rounded-3xl overflow-hidden bg-white border border-black/5"><div className="aspect-[4/3] img-skeleton" /><div className="p-5 space-y-3"><div className="h-5 w-3/4 img-skeleton rounded" /><div className="h-4 w-1/2 img-skeleton rounded" /></div></div>))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20"><p className="text-xl text-[#86868B]">{t("listings_none")}</p><p className="text-sm text-[#86868B] mt-2">{t("listings_none_hint")}</p></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((listing, i) => (
                <Reveal key={listing.id} delay={(i % 3) * 90}>
                  <ListingCard listing={listing} index={i} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
